import "@blueprintjs/select/lib/css/blueprint-select.css";
import "./Select.scss";
import {
  Select2 as BlueprintSelect2,
  ItemListRendererProps,
  ItemRendererProps,
  SelectPopoverProps,
} from "@blueprintjs/select";
import { Menu, MenuItem, Button } from "@blueprintjs/core";
import React, { isValidElement, ReactNode } from "react";
import VirtualList from "react-tiny-virtual-list";

// Redecalare forwardRef
// See: https://fettblog.eu/typescript-react-generic-forward-refs/
declare module "react" {
  // rome-ignore lint/nursery/noBannedTypes: <explanation>
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null,
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

function fastHighlightText(text: string, query: string): React.ReactNode {
  if (query.trim() === "") {
    return [text];
  }

  const queryStart = text.toLowerCase().indexOf(query.toLowerCase());

  return (
    <>
      {text.substring(0, queryStart)}
      <strong>{text.substring(queryStart, queryStart + query.length)}</strong>
      {text.substring(queryStart + query.length)}
    </>
  );
}

type CreateNewItemPosition = "first" | "last";

function compileListItems<T>(
  filteredItems: T[],
  renderCreateItem: () => JSX.Element | null | undefined,
  createNewItemPosition?: CreateNewItemPosition,
) {
  const createItem = renderCreateItem();
  return createItem
    ? createNewItemPosition === "first"
      ? [createItem, ...filteredItems]
      : [...filteredItems, createItem]
    : filteredItems;
}

const itemSize = 30;
const MAX_LIST_HEIGHT = 350;
function createListRenderer<T>(createNewItemPosition?: CreateNewItemPosition) {
  return function renderList({ filteredItems, renderItem, menuProps, renderCreateItem }: ItemListRendererProps<T>) {
    const listItems = compileListItems(filteredItems, renderCreateItem, createNewItemPosition);

    return (
      <Menu {...menuProps} className={"GoodFillBlueprintSelect__menu"}>
        <VirtualList
          height={listItems.length * itemSize < MAX_LIST_HEIGHT ? listItems.length * itemSize : MAX_LIST_HEIGHT}
          itemCount={listItems.length}
          itemSize={itemSize}
          renderItem={({ index, style }) => {
            const item = listItems[index];
            // If item is the createItem MenuItem, just return it.
            if (isValidElement(item)) {
              return item;
            }

            const renderedItem = renderItem(item as T, index);
            // This `renderedItem?.props` hack is necessary to pass VirtualList styles down into the MenuItem
            return <MenuItem {...renderedItem?.props} key={index} style={style} />;
          }}
        />
      </Menu>
    );
  };
}

function createItemRenderer<T>(itemToStringId: (item: T) => string, itemToString: (item: T) => string) {
  function createMenuItemRenderer(item: T, itemProps: ItemRendererProps) {
    return (
      <MenuItem
        active={itemProps.modifiers.active}
        disabled={itemProps.modifiers.disabled}
        key={itemToStringId(item)}
        onClick={itemProps.handleClick}
        text={fastHighlightText(itemToString(item), itemProps.query)}
        title={itemToString(item)}
      />
    );
  }

  return createMenuItemRenderer;
}

const POPOVER_PROPS_DEFAULT: SelectPopoverProps["popoverProps"] = {
  minimal: true,
  captureDismiss: true,

  // make this false by default to make it work in narrow table filters
  // for other uses, where its too big, and we don't mind horizontal scroll
  // we can set it to true
  matchTargetWidth: false,
};

function createItemPredicate<T>(itemToString: (item: T) => string) {
  return (query: string, item: T) => itemToString(item).toLowerCase().includes(query.toLowerCase());
}

export type BlueprintSelectProps<T> = {
  items: T[];
  selected: T | null;
  onSelect: (item: T) => void;
  itemToStringId: (item: T) => string;
  itemToStringForList: (item: T) => string;
  itemToStringForSelected: (item: T) => string;
  disabled?: boolean;
  popoverProps?: SelectPopoverProps["popoverProps"];
  placeholder: string;
  filterInputPlaceholder?: string;
  createNewItemFromQuery?: (query: string) => T;
  createNewItemRenderer?: (
    query: string,
    active: boolean,
    handleClick: React.MouseEventHandler<HTMLElement>,
  ) => JSX.Element | undefined;
  createNewItemPosition?: CreateNewItemPosition;
  children?: ReactNode;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  ref?: React.LegacyRef<Button>;
};

export const Select = React.forwardRef(
  <T extends unknown>(
    {
      disabled,
      onSelect,
      selected,
      items,
      itemToStringId,
      itemToStringForList,
      itemToStringForSelected,
      popoverProps,
      placeholder,
      filterInputPlaceholder,
      createNewItemFromQuery,
      createNewItemRenderer,
      createNewItemPosition,
      children,
      onBlur,
    }: BlueprintSelectProps<T>,
    ref: React.LegacyRef<Button>,
  ) => (
    <BlueprintSelect2<T>
      disabled={disabled}
      onItemSelect={onSelect}
      items={items}
      itemRenderer={createItemRenderer(itemToStringId, itemToStringForList)}
      itemListRenderer={createListRenderer(createNewItemPosition)}
      itemPredicate={createItemPredicate(itemToStringForList)}
      popoverProps={{
        ...POPOVER_PROPS_DEFAULT,
        ...(popoverProps !== undefined ? popoverProps : {}),
      }}
      inputProps={{
        placeholder: filterInputPlaceholder,
        onBlur: onBlur,
      }}
      createNewItemFromQuery={createNewItemFromQuery}
      createNewItemRenderer={createNewItemRenderer}
    >
      {children || (
        <Button rightIcon="double-caret-vertical" ref={ref}>
          {selected !== null ? itemToStringForSelected(selected) : placeholder}
        </Button>
      )}
    </BlueprintSelect2>
  ),
);
